import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonListComp = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Functional Power</div>
                <h1>List Comprehensions</h1>
                <p className="hero-lead">
                    Python's signature feature. Learn to replace clunky loops with elegant, mathematical one-liners
                    that are not just shorter, but significantly faster at the C-level.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    If you show a C++ or Java developer a Python codebase, the first thing that confuses them is the "List Comprehension".
                    It looks like a loop inside a list.
                </p>
                <p>
                    In reality, it is a declarative way to <strong>construct</strong> a list.
                    Instead of telling the computer <em>how</em> to build the list (step-by-step appending),
                    you describe <em>what</em> the list should be. This shift from Imperative to Declarative thinking
                    is the hallway that leads to becoming a Senior Python Engineer.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Syntax Anatomy:</strong> The `[expression for item in iterable]` formula.</li>
                        <li><strong>Performance:</strong> Why they are faster than `for` loops (Bytecode analysis).</li>
                        <li><strong>Filtering:</strong> Integrating `if` statements concisely.</li>
                        <li><strong>Nested Loops:</strong> Flattening matrices in one line.</li>
                        <li><strong>Readability:</strong> When NOT to use them.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Mental Model: Assembly Line vs Shopping Cart</h2>
                <p>
                    To understand why comprehensions are special, let's use an analogy.
                </p>

                <h3>1. The "Shopping Cart" (For Loop)</h3>
                <p>
                    A standard `for` loop is like pushing a shopping cart through a store.
                    You stop at each shelf, pick up an item, and manually place it in the cart (`append`).
                    It's a manual process repeated for every item.
                </p>
                <CodeBlock language="python" code={`# Imperative Style
cart = []
for item in store_items:
    if item.is_fresh():
        cart.append(item.buy())`} />

                <h3>2. The "Assembly Line" (Comprehension)</h3>
                <p>
                    A list comprehension is an industrial assembly line. You define the blueprint at the start.
                    The raw materials (`store_items`) are fed in, defective ones are rejected (`if`),
                    and the final product is stamped out automatically. There is no manual "placing" step.
                </p>
                <CodeBlock language="python" code={`# Declarative Style
cart = [item.buy() for item in store_items if item.is_fresh()]`} />
            </div>

            <div className="content-section">
                <h2>Syntax Deep Dive</h2>
                <p>
                    The syntax can be broken down into three parts: <strong>Output</strong>, <strong>Input</strong>, and <strong>Predicate</strong>.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>The Formula</span>
                    </div>
                    <CodeBlock language="python" code={`# [   OUTPUT    |      INPUT      |   PREDICATE   ]
# [ expression  for var in source  if condition   ]

numbers = [1, 2, 3, 4, 5]

# 1. Basic Mapping
squares = [n * n for n in numbers] 
# Output: [1, 4, 9, 16, 25]

# 2. Variable Transformation
#    Expression can be a function call, string formatting, etc.
hex_codes = [hex(n) for n in numbers]
# Output: ['0x1', '0x2', '0x3', '0x4', '0x5']`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Filtering items (`if` clause)</h2>
                <p>
                    You can filter items by adding an `if` at the end. Note that this `if` is essentially a "filter"
                    that runs <em>before</em> the expression is calculated.
                </p>
                <CodeBlock language="python" code={`# Get only even squares
# Note: The if statement does NOT have an 'else' here!
# We are filtering, not choosing values.
even_squares = [n*n for n in numbers if n % 2 == 0]

# Output: [4, 16]`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Common Confusion:</strong> Where does the `else` go?
                        <br />
                        If you are <strong>filtering</strong> (skipping items), the `if` goes at the <strong>end</strong>.
                        <br />
                        If you are <strong>choosing</strong> (ternary operator), the `if/else` goes at the <strong>start</strong> (in the expression).
                    </div></ProTip>

                <CodeBlock language="python" code={`# Choosing (Ternary Operator)
# "Mark even numbers as 0, keep odds as is"
# We keep ALL items, but change their value.
masked = [0 if n % 2 == 0 else n for n in numbers]
# Output: [1, 0, 3, 0, 5]`} />
            </div>

            <div className="content-section">
                <h2>Performance: Why Comprehensions are Faster</h2>
                <p>
                    You will often hear that comprehensions are "faster" than loops. But why?
                    They both do O(N) work. The answer lies in the <strong>C-level implementation</strong>.
                </p>
                <p>
                    Let's look at the Bytecode (what the Python interpreter actually executes).
                </p>

                <h3>1. The For Loop Bytecode</h3>
                <p>
                    In a loop, Python has to look up the `.append` attribute on the list object <em>every single iteration</em>.
                </p>
                <CodeBlock language="python" code={`# Looping overhead:
# 1. LOAD_NAME 'sq'
# 2. LOAD_METHOD 'append' (Slow attribute lookup!)
# 3. CALL_METHOD
# 4. POP_TOP`} />

                <h3>2. The Comprehension Bytecode</h3>
                <p>
                    The compiler knows you are building a list. It optimizes the process using a special bytecode instruction
                    called `LIST_APPEND`. It bypasses the attribute lookup entirely and writes directly to the C-struct.
                </p>
                <CodeBlock language="python" code={`# Comprehension optimization:
# 1. LIST_APPEND (Fast C-level operation)`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Benchmark:</strong> In simple cases, list comprehensions are roughly <strong>15-25% faster</strong> than equivalent `for` loops.
                        However, if your loop body is complex (Database calls, I/O), this speedup is negligible. Use them for style, not just speed.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Advanced: Flattening & Nested Loops</h2>
                <p>
                    Here is where it gets tricky. You can put multiple `for` clauses in a single comprehension.
                    They execute from left to right, exactly like nested loops.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Matrix Flattening</span>
                    </div>
                    <CodeBlock language="python" code={`matrix = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9]
]

#  Nested Loop Way
flat = []
for row in matrix:
    for num in row:
        flat.append(num)

# Comprehension Way
# Read it like English: "num FOR row in matrix FOR num in row"
flat = [num for row in matrix for num in row]
# Output: [1, 2, 3, 4, 5, 6, 7, 8, 9]`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Scope Leakage (Python 2 vs 3)</h2>
                <p>
                    In Python 2, list comprehensions leaked their loop variable into the global scope. This was a major source of bugs.
                    Python 3 fixed this; variables inside the comprehension are local.
                </p>
                <CodeBlock language="python" code={`x = "Global"

# Python 3
dummy = [x for x in range(5)]
print(x) # Output: "Global" (Safe!)

# Python 2 (Historical)
# print(x) # Output: 4 (The loop overwrote it!)`} />
            </div>

            <div className="content-section common-pitfalls">
                <h2>Common Pitfalls</h2>

                <div className="pitfall-card">
                    <h3> The "Unreadable One-Liner"</h3>
                    <p><strong>The Trap:</strong> Just because you <em>can</em> do it in one line, doesn't mean you <em>should</em>.</p>
                    <CodeBlock language="python" code={`#  What does this even do?
result = [x*y for x in range(10) if x &gt; 5 for y in range(5) if x+y < 12]

# Use a loop for complex logic
result = []
for x in range(10):
    if x &gt; 5:
        for y in range(5):
            if x+y < 12:
                result.append(x*y)`} />
                    <p className="caption">Rule of thumb: If it wraps to a second line, use a real loop.</p>
                </div>

                <div className="pitfall-card">
                    <h3> Side Effects in Comprehensions</h3>
                    <p><strong>The Trap:</strong> Using comprehensions just to run a function (like `print`) without using the resulting list.</p>
                    <CodeBlock language="python" code={`#  Creates a useless list of [None, None, None...]
[print(x) for x in range(10)] 

# Use a loop for actions
for x in range(10):
    print(x)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Modern Python: The Walrus Operator</h2>
                <p>
                    Python 3.8's Assignment Expression (`:=`) works perfectly inside comprehensions.
                    This is useful when you need to calculate a value, check it, and then use it.
                </p>
                <CodeBlock language="python" code={`import random
def expensive_calc(x):
    return x * random.random()

# Without Walrus (Calculates twice!)
# result = [expensive_calc(x) for x in range(10) if expensive_calc(x) &gt; 0.5]

# With Walrus (Calculates once, captures value)
result = [val for x in range(10) if (val := expensive_calc(x)) &gt; 0.5]`} />
            </div>

        </div>
    );
};

export default PythonListComp;


